import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  attemptsApi,
  resultHistoryApi,
} from '../../api/endpoints.js';
import {
  ActionButton,
  Alert,
  ErrorState,
  LoadingState,
  Panel,
  StatusBadge,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { ReadinessBanner } from './ReadinessBanner.js';
import { ReviewActions, CorrectionAction } from './ResultActions.js';
import {
  KeyResultsPanel,
  LockNotice,
  ResultIdentity,
  ResultRecordPanels,
  VerificationChecklistPanel,
  latestRevision,
} from './review-parts.js';
import { BenchLayout } from './bench-parts.js';
import { testName, toneForAttemptStatus } from './status.js';

/**
 * REVIEWING ONE RESULT — module C7.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTIONS 4, 5 AND 8 BEFORE ADDING ANYTHING TO THIS PAGE.
 * ===========================================================================
 *
 * What a reviewer is given, and nothing beyond it: the record exactly as it was
 * submitted, what changed since the previous submission, every correction on
 * file and the whole recorded history — every revision, every review act, every
 * approval act, each with who and when. Earlier content and earlier return
 * reasons are never hidden and never summarised.
 *
 * THERE IS NO SCIENCE ON THIS PAGE. No reading, no unit, no formula, no limit,
 * no chart and no pass or fail, for any test. A review says a RECORD was
 * checked; the readiness banner says plainly that the test has no certified
 * specialised template and that the general path calculates nothing.
 *
 * THE CONTROLS ARE NOT THE AUTHORIZATION. `canReview` already combines the
 * permission, the state and "not your own work", and the endpoints behind every
 * button re-check all three — with a database trigger refusing self-review
 * independently of the API.
 */
export function ReviewPage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const history = useQuery({
    queryKey: ['result-history', attemptId],
    queryFn: () => resultHistoryApi.get(attemptId as string),
  });
  const attempt = useQuery({
    queryKey: ['attempt', attemptId],
    queryFn: () => attemptsApi.get(attemptId as string),
  });

  if (history.isLoading || attempt.isLoading) {
    return <LoadingState label={t('common.loading')} />;
  }
  if (history.error) {
    return <ErrorState error={history.error} onRetry={() => void history.refetch()} />;
  }
  if (attempt.error) {
    return <ErrorState error={attempt.error} onRetry={() => void attempt.refetch()} />;
  }
  const record = history.data;
  const work = attempt.data;
  if (!record || !work) return null;

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['result-history', attemptId] });
    await queryClient.invalidateQueries({ queryKey: ['attempt', attemptId] });
    await queryClient.invalidateQueries({ queryKey: ['review-queue'] });
    await queryClient.invalidateQueries({ queryKey: ['approval-queue'] });
    await queryClient.invalidateQueries({ queryKey: ['assignments'] });
    await queryClient.invalidateQueries({ queryKey: ['assignment', record.assignmentId] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const latest = latestRevision(record);

  return (
    <>
      <header className="mb-gutter flex flex-wrap items-start gap-stack">
        <ResultIdentity
          sampleId={work.assignment.sample.id}
          sampleNumber={work.assignment.sample.sampleNumber}
          pieceCode={work.assignment.pieceCode}
          testLabel={testName(work.assignment.test, locale)}
          attemptNumber={work.attemptNumber}
          performedBy={record.performedBy.displayName ?? ''}
          status={
            <StatusBadge
              label={t(`attemptStatus.${record.attemptStatus}`)}
              tone={toneForAttemptStatus(record.attemptStatus)}
            />
          }
        />
        <ActionButton priority="tertiary" onPress={() => navigate('/review')}>
          {t('review.backToQueue')}
        </ActionButton>
      </header>

      <div className="flex flex-col gap-gutter">
        <ReadinessBanner readiness={work.assignment.test.templateReadiness} />

        <Alert
          tone="info"
          title={t('review.recordOnlyTitle')}
          description={t('review.recordOnlyBody')}
        />

        {/*
          Shown rather than hidden: a person who submitted this needs to know
          why the buttons are absent, and "the system refuses your own work" is
          a better answer than a blank space (D108).
        */}
        {record.callerIsPerformer ? (
          <Alert
            tone="warning"
            title={t('review.ownWorkTitle')}
            description={t('review.ownWorkBody')}
          />
        ) : null}

        <LockNotice lock={record.lock} />

        <BenchLayout
          main={
            <>
              <KeyResultsPanel revision={latest} />
              <ResultRecordPanels history={record} />
            </>
          }
          side={
            <>
              <VerificationChecklistPanel history={record} />
              <Panel title={t('bench.decisionTitle')} description={t('bench.reviewDecisionHint')}>
                <div className="flex flex-col gap-stack">
                  {record.canReview ? (
                    <ReviewActions history={record} onActed={refresh} />
                  ) : (
                    <Alert
                      tone="neutral"
                      title={t('review.noActionTitle')}
                      description={t('review.noActionBody')}
                    />
                  )}

                  {/*
                    A post-approval correction. Shown only where the backend says this
                    caller may open one — and refused server-side regardless.
                  */}
                  {record.canCorrect ? (
                    <div className="flex flex-wrap items-center justify-end gap-inline">
                      <CorrectionAction history={record} onActed={refresh} />
                    </div>
                  ) : null}
                </div>
              </Panel>
            </>
          }
        />
      </div>
    </>
  );
}
